import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@anytag/shared-atoms';
import { cn } from '@anytag/shared-utils';
import { MarkdownReader } from './markdown-reader';

export interface TemplatesProps {
  onSelect: (template: string) => void;
}

export const Templates = ({ onSelect }: TemplatesProps) => {
  const { t, i18n } = useTranslation();

  const templates: Record<string, Array<{ title: string; subtitle: string; details: string }>> = {
    ja: [
      {
        title: t('Audience Analysis'),
        subtitle: t('Understand the characteristics, preferences'),
        details:
          '日本のコスメブランドのプロモーションで、美容に特化した日本人女性インフルエンサーを探しています。以下のような条件で探してもらえますか？\n\n・年齢：20代前半〜30代前半くらい\n\n・性別：女性\n\n・SNS：Instagram\n\n・フォロワー数：1万〜10万人\n\n・カテゴリ：美容（スキンケア、メイク、フェムテックなど）\n\n投稿内容にナチュラルメイクや毎日のスキンケアルーティンが含まれているとベストです。'
      },
      {
        title: t('KOLs Search'),
        subtitle: t('Quickly finding information about key opinion leaders'),
        details: `人気の美容液をPRするキャンペーンに最適な日本の女性インフルエンサーをリストアップしてください！  \n\n▼ブランドについて\n\n
 ”肌に触れるものは何より清くあるべき”というブランドスローガンを掲げ、天然成分に基づいた高品質の製品を提供しております。 清く美しい太古の自然の力をあなたのお肌にお届けし、肌本来の力を引き出します。 \n\n▼PRする商品 \n\nヒアルーシカ サンセラム\n\n
1. 日焼け止め特有のキシみや匂いがせず、白浮きしない！ \n\n2. べたつきとはさよなら！セラムような軽い付け心地 \n\n3. SPF50+PA++++の強力な紫外線保護効果 \n\n4. ヒアルロン酸とツボクサエキス配合で乾燥した肌に水分チャージ \n\n
▼ターゲットイメージ \n\n美容感度がとくに高い、紫外線対策に気を使っている女性 \n\n
▼投稿イメージ \n\n美容液のパッケージと肌にのせた
        `
      }
    ],
    en: [
      {
        title: t('Audience Analysis'),
        subtitle: t('Understand the characteristics, preferences'),
        details:
          'We are looking for Thailand-based female beauty influencers to collaborate with a premium skincare brand. Please help us find suitable candidates with the following criteria:\n\n・Location: Thailand\n\n・Gender: Female\n\n・Age group: Early 20s to early 30s\n\n・Main platform: Instagram\n\n・Follower count: 10K–100K\n\n・Content focus: Beauty, including skincare, makeup, or personal care\n\nClean, soft-toned feeds with natural lighting and close-up skin visuals are highly preferred.'
      },
      {
        title: t('KOLs Search'),
        subtitle: t('Quickly finding information about key opinion leaders'),
        details: `We are planning a new influencer campaign in Thailand for a lightweight serum-based sunscreen from a natural beauty brand.We’d love to work with creators who care deeply about skincare quality and understand how to communicate both product benefits and brand story.\n\n▼About the Brand\n\nThe brand believes that “what touches your skin should be pure above all else.”It’s committed to formulating skincare with high-quality natural ingredients that bring out the skin’s original strength, especially in hot and humid weather.
  \n\n▼Featured Product\n\nHialucia Sun Serum\n\n・No greasy feel, no white cast, no sunscreen smell\n\n・Light and breathable — feels more like a serum than SPF\n\n・SPF50+ PA++++ for strong sun protection\n\n・Formulated with Hyaluronic Acid and Centella Asiatica for extra hydration\n\n▼Target Audience\n\nWomen in Thailand who are serious about skincare and sun protection, especially those with sensitive or combination skin.\n\n▼ Posting Style\n\nClean product shots showing the texture and application\n\nPlease shortlist creators who match this brand tone and product fit.\n\n※You can attach the image.`
      }
    ]
  };

  const defaultTemplates = templates[i18n.language] || templates.en;

  return (
    <div className="max-h-full min-h-full space-y-4 overflow-y-auto px-4 py-6">
      <h3 className="text-lg">{t('Hi, I’m your AI Agent')}</h3>
      <p className="text-sm text-tertiary">{t('What do you wanna do')}:</p>
      {defaultTemplates.map((template, index) => (
        <Accordion key={index} type="single" collapsible className="overflow-y-auto rounded-lg border-base-border">
          <AccordionItem
            key={template.title}
            value={template.title}
            className={cn(
              '[&[data-state="closed"]]:hover:[&_*]:bg-light-gray',
              '[&[data-state="closed"]]:hover:bg-light-gray'
            )}
          >
            <AccordionTrigger hasIcon={false} hasHover={false} className="box-content p-4">
              <div>
                <h3 className="text-base font-semibold">{template.title}</h3>
                <p className="text-sm text-tertiary">{template.subtitle}</p>
              </div>
            </AccordionTrigger>

            <AccordionContent
              onClick={() => onSelect(template.details)}
              className="grid cursor-pointer gap-4 rounded-sm border-t-0 bg-white py-2 hover:bg-light-gray hover:[&_div]:bg-light-gray"
            >
              <MarkdownReader value={template.details} className="[&_li]:text-base [&_p]:text-base" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ))}
    </div>
  );
};
